import { CELL_FORMATS } from 'common/constants';
import { BasePercentiles, CustomPercentile, TypeOfPay } from 'types';
import { upperFirst } from 'utils/string';

export enum JobMode {
  JOBS = 'companyJobs',
  JOBS_FAMILY = 'companyJobsFamily',
  JOBS_SUB_FAMILY = 'companyJobsSubFamily',
}

export const jobModeOptions = [
  { label: 'Company Jobs', value: JobMode.JOBS },
  { label: 'Company Families', value: JobMode.JOBS_FAMILY },
  { label: 'Company Sub-Families', value: JobMode.JOBS_SUB_FAMILY },
];

export const typeOfPayTitle: { [K in TypeOfPay]: string } = {
  [TypeOfPay.BASE]: 'Base Salary',
  [TypeOfPay.ACTUAL_BONUS]: 'Actual Bonus',
  [TypeOfPay.ACTUAL_BONUS_PCT]: 'Actual Bonus %',
  [TypeOfPay.TARGET_BONUS]: 'Target Bonus',
  [TypeOfPay.TARGET_BONUS_PCT]: 'Target Bonus %',
  [TypeOfPay.TARGET_TOTAL_CASH]: 'Target Total Cash',
  [TypeOfPay.ACTUAL_TOTAL_CASH]: 'Actual Total Cash',
  [TypeOfPay.TARGET_TOTAL_DIRECT]: 'Target Total Direct',
  [TypeOfPay.ACTUAL_TOTAL_DIRECT]: 'Actual Total Direct',
  [TypeOfPay.LTI_ACTUAL]: 'LTI Actual',
  [TypeOfPay.LTI_ACTUAL_PCT]: 'LTI Actual %',
  [TypeOfPay.FIXED_COMP]: 'Fixed Comp',
  [TypeOfPay.TOTAL_ALLOWANCES]: 'Total Allowances',
  [TypeOfPay.LTI_TARGET]: 'LTI Target',
  [TypeOfPay.LTI_NEW_HIRE_ACTUAL]: 'LTI New Hire Actual',
  [TypeOfPay.LTI_NEW_HIRE_TARGET]: 'LTI New Hire Target',
  [TypeOfPay.SALES_INCENTIVE_ACTUAL]: 'Sales Incentive Actual',
  [TypeOfPay.SALES_INCENTIVE_TARGET]: 'Sales Incentive Target',
  [TypeOfPay.SALES_COMMISSION_PAID_ACTUAL]: 'Sales Commissions Paid Actual',
  [TypeOfPay.ANNUAL_BASE]: 'Annual Base',
  [TypeOfPay.TRANSPORTATION_ALLOWANCE]: 'Transportation Allowance',
  [TypeOfPay.HOUSING_ALLOWANCE]: 'Housing Allowance',
  [TypeOfPay.MEAL_ALLOWANCE]: 'Meal Allowance',
  [TypeOfPay.EDUCATION_ALLOWANCE]: 'Education Allowance',
  [TypeOfPay.LTI_TARGET_PCT]: 'LTI Target %',
  [TypeOfPay.SALES_INCENTIVE_PCT_ACTUAL]: 'Sales Incentive Actual %',
  [TypeOfPay.SALES_INCENTIVE_PCT_TARGET]: 'Sales Incentive Target %',
  [TypeOfPay.SALES_COMMISSION_TARGET_PCT]: 'Sales Commission Target %',
  [TypeOfPay.JOB_SKILL_ALLOWANCE]: 'Job Skill Allowance',
  [TypeOfPay.LANGUAGE_ALLOWANCE]: 'Language Allowance',
  [TypeOfPay.LEAVE_TRAVEL_ALLOWANCE]: 'Leave Travel Allowance',
  [TypeOfPay.TELECOMMUNICATION_PHONE_ALLOWANCE]: 'Telecommunication Phone Allowance',
  [TypeOfPay.CAR_ALLOWANCE]: 'Car Allowance',
}

export const typeOfPayOptions: { value: TypeOfPay, label: string }[] = Object.entries(typeOfPayTitle)
  .map(([value, label]) => ({ value: value as TypeOfPay, label }));

export const typeOfPayFormats: { [K in TypeOfPay]: typeof CELL_FORMATS[keyof typeof CELL_FORMATS] } = {
  [TypeOfPay.BASE]: CELL_FORMATS.currency,
  [TypeOfPay.ACTUAL_BONUS]: CELL_FORMATS.currency,
  [TypeOfPay.ACTUAL_BONUS_PCT]: CELL_FORMATS.percentage,
  [TypeOfPay.TARGET_BONUS]: CELL_FORMATS.currency,
  [TypeOfPay.TARGET_BONUS_PCT]: CELL_FORMATS.percentage,
  [TypeOfPay.TARGET_TOTAL_CASH]: CELL_FORMATS.currency,
  [TypeOfPay.ACTUAL_TOTAL_CASH]: CELL_FORMATS.currency,
  [TypeOfPay.TARGET_TOTAL_DIRECT]: CELL_FORMATS.currency,
  [TypeOfPay.ACTUAL_TOTAL_DIRECT]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_ACTUAL]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_ACTUAL_PCT]: CELL_FORMATS.percentage,
  [TypeOfPay.FIXED_COMP]: CELL_FORMATS.currency,
  [TypeOfPay.TOTAL_ALLOWANCES]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_TARGET]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_NEW_HIRE_ACTUAL]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_NEW_HIRE_TARGET]: CELL_FORMATS.currency,
  [TypeOfPay.SALES_INCENTIVE_ACTUAL]: CELL_FORMATS.currency,
  [TypeOfPay.SALES_INCENTIVE_TARGET]: CELL_FORMATS.currency,
  [TypeOfPay.SALES_COMMISSION_PAID_ACTUAL]: CELL_FORMATS.currency,
  [TypeOfPay.ANNUAL_BASE]: CELL_FORMATS.currency,
  [TypeOfPay.TRANSPORTATION_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.HOUSING_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.MEAL_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.EDUCATION_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.LTI_TARGET_PCT]: CELL_FORMATS.percentage,
  [TypeOfPay.SALES_INCENTIVE_PCT_ACTUAL]: CELL_FORMATS.percentage,
  [TypeOfPay.SALES_INCENTIVE_PCT_TARGET]: CELL_FORMATS.percentage,
  [TypeOfPay.SALES_COMMISSION_TARGET_PCT]: CELL_FORMATS.percentage,
  [TypeOfPay.JOB_SKILL_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.LANGUAGE_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.LEAVE_TRAVEL_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.TELECOMMUNICATION_PHONE_ALLOWANCE]: CELL_FORMATS.currency,
  [TypeOfPay.CAR_ALLOWANCE]: CELL_FORMATS.currency,
}

// Map of TypeOfPay fields to their fraction digits for percentage display
export const PERCENTAGE_FRACTION_DIGITS_MAP: Partial<Record<TypeOfPay, number>> = {
  [TypeOfPay.TARGET_BONUS_PCT]: 3,
};

export const DEFAULT_PERCENTAGE_FRACTION_DIGITS = 1;

const BASE_PERCENTILES = Object.values(BasePercentiles)
  .filter((percentile) => percentile !== BasePercentiles.CP1);

export type GetPercentilesParams = {
  typeOfPay: TypeOfPay,
  basePercentiles?: typeof BASE_PERCENTILES,
  customPercentiles?: CustomPercentile[]
}

type PercentileKey = string;

export const getPercentiles = ({
  typeOfPay,
  basePercentiles = BASE_PERCENTILES,
  customPercentiles = []
}: GetPercentilesParams): { order: number; key: PercentileKey }[] => {
  const baseItems = basePercentiles
    .map((percentile) => ({
      order: +percentile,
      key: `${typeOfPay}${percentile}th`
    }));

  const customItems = customPercentiles
    .filter(customPercentile => customPercentile[typeOfPay])
    .map(customPercentile => ({
      order: customPercentile[typeOfPay] || 0,
      key: `${typeOfPay}${customPercentile.columnPostfix}`,
    }))

  return [
    ...baseItems,
    ...customItems,
  ].sort((a, b) =>
    (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0)
  );
};

export type PercentileColumn = {
  key: PercentileKey
  title: string
  type: typeof CELL_FORMATS[keyof typeof CELL_FORMATS]
};

type PercentileFieldsGroup = {
  key: TypeOfPay,
  title: string,
  editTitle: string,
  columns: PercentileColumn[]
}

export type PercentileFields = {
  [K in TypeOfPay]: PercentileFieldsGroup
}

type GetPercentileFieldsParams = {
  basePercentiles?: typeof BASE_PERCENTILES,
  customPercentiles?: CustomPercentile[],
}

export const getPercentileFields = ({
  basePercentiles = BASE_PERCENTILES,
  customPercentiles = [],
}: GetPercentileFieldsParams): PercentileFields => {
  const res = Object.values(TypeOfPay).reduce(
    (res: Partial<PercentileFields>, typeOfPay: TypeOfPay) => {
      const config: PercentileFieldsGroup = {
        key: typeOfPay,
        title: typeOfPayTitle[typeOfPay],
        editTitle: typeOfPayTitle[typeOfPay],
        columns: getPercentiles({ basePercentiles, customPercentiles, typeOfPay }).map((item) => ({
          key: item.key,
          title: `${item.order}th`,
          type: typeOfPayFormats[typeOfPay]
        }))
      }

      return {
        ...res,
        [typeOfPay]: config
      }
    }, {})

  return res as PercentileFields
};

export type AdditionalFields = {
  [K in TypeOfPay]: PercentileColumn[]
}

export const getAdditionalFields = () => {
  const res = Object.values(TypeOfPay).reduce<Partial<AdditionalFields>>(
    (res, typeOfPay) => {
      const columns: PercentileColumn[] = [
        { key: `${typeOfPay}Avg`, title: 'Avg', type: typeOfPayFormats[typeOfPay] },
        { key: `${typeOfPay}MRPPercentile`, title: 'MRP %ile', type: CELL_FORMATS.number },
        { key: `${typeOfPay}MRP`, title: 'MRP', type: typeOfPayFormats[typeOfPay] },
        { key: `avgCompany${upperFirst(typeOfPay)}`, title: 'Avg Company', type: typeOfPayFormats[typeOfPay] },
      ];

      if (typeOfPayFormats[typeOfPay] === CELL_FORMATS.currency) {
        columns.push({ key: `${typeOfPay}PctToMRP`, title: '% To MRP', type: CELL_FORMATS.percentage });
      }

      return ({
        ...res,
        [typeOfPay]: columns,
      })
    }, {});

  return res as AdditionalFields;
};

const availableCustomPercentiles = [BasePercentiles.CP1];

export function buildDataPoint(typeOfPay: TypeOfPay, percentile: BasePercentiles = BasePercentiles._50) {
  const postfix = availableCustomPercentiles.includes(percentile) ? '' : 'th';
  return `${typeOfPay}${percentile}${postfix}`;
}

export const targetPercentileOptions = Object.values(BasePercentiles).map((percentile) => ({
  value: percentile,
  label: percentile === BasePercentiles.CP1 ? 'Custom Percentile' : percentile,
}));
